export type GlucoseLocale = "en" | "fr";

export interface GlucoseCopy {
    languageLabel: string;
    english: string;
    french: string;
    longDate: string;
    weekdays: [string, string, string, string, string, string, string];
    common: {
        loading: string;
        checkingAuth: string;
        backHome: string;
        tryAgain: string;
        yes: string;
        no: string;
        previous: string;
        next: string;
        saving: string;
        submit: string;
        sending: string;
        selectOption: string;
    };
    home: {
        title: string;
        subtitle: string;
        join: string;
        learnMore: string;
        publicNote: string;
        whatYouGet: string;
        benefits: string[];
        inclusion: string;
        criteria: string[];
        howItWorks: string;
        wearTitle: string;
        wearBody: string;
        checkInTitle: string;
        checkInBody: string;
        meetingsTitle: string;
        meetingsBody: string;
        whyTitle: string;
        whyIntro: string;
        restrictionsTitle: string;
        restrictions: string[];
        solutionTitle: string;
        solutionBody: string;
        ctaTitle: string;
        ctaBody: string;
        footer: string;
    };
    getStarted: {
        title: string;
        subtitle: string;
        waitlistTitle: string;
        waitlistDescription: string;
        waitlistPoints: string[];
        joinWaitlist: string;
        studyTitle: string;
        studyDescription: string;
        studyPoints: string[];
        begin: string;
        backHome: string;
    };
    waitlist: {
        successTitle: string;
        successDescription: string;
        backHome: string;
        title: string;
        description: string;
        firstName: string;
        lastName: string;
        email: string;
        joining: string;
        join: string;
        back: string;
    };
    login: {
        title: string;
        description: string;
        loading: string;
        phoneRequired: string;
        codeSent: string;
        codeSentDescription: string;
        sendFailed: string;
        sendCode: string;
    };
    verify: {
        codeLength: string;
        title: string;
        description: (phone: string) => string;
        resent: string;
        resentDescription: string;
        resendFailed: string;
        verifying: string;
        verify: string;
        resend: string;
        changePhone: string;
    };
    profile: {
        checking: string;
        registeredTitle: string;
        registeredDescription: string;
        continue: string;
        title: string;
        description: string;
        joining: string;
        join: string;
    };
    instructions: {
        title: string;
        intro: string;
        purposeTitle: string;
        purposeBody: string;
        formsTitle: string;
        formsBody: string;
        reportTitle: string;
        atSea: string;
        ifYes: string;
        glucose: string;
        other: string;
        quickTitle: string;
        quickBody: string;
        helpTitle: string;
        helpBody: string;
        direct: string;
        mobile: string;
        duration: string;
        projectId: string;
        logout: string;
    };
    formShell: {
        verifyingTitle: string;
        verifyingDescription: string;
        checkingAccess: string;
        notFoundTitle: string;
        notFoundDescription: string;
        deniedTitle: string;
        deniedDescription: string;
        title: (date: string) => string;
        description: string;
        start: string;
        seeEntries: string;
        saveFailed: string;
    };
    atSea: {
        question: string;
        yes: string;
        no: string;
    };
    atSeaAllDay: {
        question: string;
        yes: string;
        no: string;
    };
    intervals: {
        startRequired: string;
        endRequired: string;
        endAfterStart: string;
        atLeastOne: string;
        noOverlap: string;
        afterPrevious: string;
        beforeNext: string;
        question: string;
        add: string;
        title: (index: number) => string;
        remove: string;
        startLabel: string;
        endLabel: string;
        startPlaceholder: string;
        endPlaceholder: string;
    };
    duty: {
        atLeastOne: string;
        noOverlap: string;
        notOnWater: string;
        chooseStartFirst: string;
        startOutside: string;
        endAfterStart: string;
        withinSeaTime: string;
        noneRecorded: string;
        notAtSea: string;
        question: string;
        todayLabel: string;
        match: string;
        add: string;
    };
    glucose: {
        question: string;
        hint: string;
        high: string;
        highDescription: string;
        low: string;
        lowDescription: string;
        both: string;
        bothDescription: string;
        none: string;
        noneDescription: string;
    };
    notes: {
        tooLong: string;
        question: string;
        hint: string;
        placeholder: string;
        max: string;
        count: (length: number) => string;
    };
    steps: {
        atSea: string;
        allDay: string;
        timeAtSea: string;
        onDuty: string;
        glucose: string;
        notes: string;
        inProgress: string;
        completed: string;
        skipped: string;
        pending: string;
        loadError: string;
        progress: (current: number, total: number) => string;
    };
    complete: {
        loadingTitle: string;
        loadingDescription: string;
        confirming: string;
        entryTitle: string;
        entryDescription: string;
        nextOn: (date: string) => string;
        incomplete: (count: number) => string;
        showEntries: string;
        showAll: (count: number) => string;
        completeMissing: string;
        exit: string;
        formTitle: string;
        formDescription: string;
        exitForm: string;
        seeEntries: string;
    };
    entries: {
        loadingTitle: string;
        loadingDescription: string;
        pleaseWait: string;
        errorTitle: string;
        errorDescription: string;
        title: string;
        description: string;
        legend: string;
        complete: string;
        incomplete: string;
        today: string;
        emptyTitle: string;
        emptyBody: string;
        daysSelected: (count: number) => string;
        clear: string;
        creating: string;
        open: string;
        createAndOpen: string;
        markAs: string;
        atSea: string;
        notAtSea: string;
        noneSelected: string;
        openOne: string;
        createFailed: string;
        updateFailed: string;
        markedNotAtSea: (count: number) => string;
        markedAtSea: (count: number) => string;
        futureTitle: string;
        futureBody: string;
        ok: string;
    };
    loggedOut: {
        title: string;
        description: string;
    };
    unsubscribe: {
        loadingTitle: string;
        loadingDescription: string;
        errorTitle: string;
        missingProfile: string;
        failed: string;
        unexpected: string;
        somethingWrong: string;
        title: string;
        description: string;
        body: string;
    };
}

const en: GlucoseCopy = {
    languageLabel: "Language",
    english: "English",
    french: "Français",
    longDate: "dddd, MMMM D, YYYY",
    weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    common: {
        loading: "Loading...",
        checkingAuth: "Checking your authentication status",
        backHome: "Back to Home",
        tryAgain: "Try Again",
        yes: "Yes",
        no: "No",
        previous: "Previous",
        next: "Next",
        saving: "Saving...",
        submit: "Submit",
        sending: "Sending...",
        selectOption: "Please select an option before continuing",
    },
    home: {
        title: "Help Seafarers with Diabetes to Work at Sea/Harbour",
        subtitle:
            "Join our research study and help our goal to create safer, more flexible work opportunities at sea/harbour for people with diabetes.",
        join: "Join the Study",
        learnMore: "Learn More",
        publicNote: "This page is public - you can explore the study without signing in.",
        whatYouGet: "What You Get",
        benefits: [
            "Free Dexcom G7 glucose monitor for 12 months (worth £1200+)",
            "Regular check-ins with medical experts",
            "Help try to improve opportunities for future seafarers with diabetes",
            "Better glucose monitoring for 1 year",
        ],
        inclusion: "Inclusion Criteria",
        criteria: [
            "Age 18 and over, any ethnicity",
            "Seafarers requiring insulin replacement therapy",
            "Holding a valid ML5 or ENG1 certificate",
            "Seafarers without ML5 or ENG1 who are working on crafts less than ten metres",
            "Able and willing to wear a Continuous Glucose Monitoring system (CGMS) for 12 months",
            "Able and willing to document out of range glucose values and activities in Glucose Watch App while at sea",
        ],
        howItWorks: "How It Works",
        wearTitle: "Wear Monitor",
        wearBody: "We provide a Dexcom G7 monitor for 1 year",
        checkInTitle: "Daily Check-in",
        checkInBody: "Text us daily at 5pm about your day",
        meetingsTitle: "4 Meetings",
        meetingsBody: "4 online meetings in one year with our team",
        whyTitle: "Why We're Doing This",
        whyIntro: "Current maritime regulations restrict seafarers with diabetes from:",
        restrictionsTitle: "Current Restrictions",
        restrictions: [
            "Can't work alone",
            "Can't do solo watchkeeping",
            "Can't work at heights",
            "Limited to ships with doctors",
        ],
        solutionTitle: "Our Solution",
        solutionBody:
            "Modern glucose monitors like the Dexcom G7 provide real-time alerts and continuous monitoring, potentially making these restrictions outdated. Our study aims to prove this with real-world data.",
        ctaTitle: "Ready to Make a Difference?",
        ctaBody: "Join our study and assist in our goal to improve opportunities for seafarers with diabetes.",
        footer: "University of Surrey • MCA • Funded by Seafarers' Charity",
    },
    getStarted: {
        title: "SeaGM Study",
        subtitle:
            "Join our research study to help us understand health outcomes at sea/harbour. Choose your next step.",
        waitlistTitle: "Step 1: Join Waitlist",
        waitlistDescription: "Express interest and get notified about the study",
        waitlistPoints: [
            "Submit your email to join the waitlist",
            "Get notified when study opens",
            "No commitment required",
        ],
        joinWaitlist: "Join Waitlist",
        studyTitle: "Step 2: Begin Study",
        studyDescription: "Complete registration and start participating",
        studyPoints: [
            "Complete consent paperwork first",
            "Register with phone number",
            "Start receiving daily health forms",
        ],
        begin: "Begin Study Registration",
        backHome: "Back to Home",
    },
    waitlist: {
        successTitle: "Successfully Joined Waitlist!",
        successDescription: "We'll notify you when the SeaGM study opens.",
        backHome: "Back to Home",
        title: "Join the Waitlist",
        description: "Enter your details to be notified when the SeaGM study opens",
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email Address",
        joining: "Joining Waitlist...",
        join: "Join Waitlist",
        back: "Back to Options",
    },
    login: {
        title: "Study Registration",
        description: "Complete your registration to begin participating in the SeaGM study",
        loading: "Loading...",
        phoneRequired: "Phone number is required",
        codeSent: "Code sent!",
        codeSentDescription: "A verification code has been sent to your phone.",
        sendFailed: "Failed to send code",
        sendCode: "Send Code",
    },
    verify: {
        codeLength: "Please enter the 6-digit code",
        title: "Verify Your Phone",
        description: (phone) => `Enter the 6-digit code sent to ${phone}`,
        resent: "Code sent!",
        resentDescription: "A new verification code has been sent to your phone.",
        resendFailed: "Failed to resend code",
        verifying: "Verifying...",
        verify: "Verify Code",
        resend: "Resend Code",
        changePhone: "Change Phone Number",
    },
    profile: {
        checking: "Just checking your profile...",
        registeredTitle: "You're Registered",
        registeredDescription: "You're all set up and enrolled in the study",
        continue: "Continue to Study",
        title: "Complete Your Registration",
        description: "Create your profile to join the SeaGM study",
        joining: "Joining Study...",
        join: "Join Study",
    },
    instructions: {
        title: "What to Expect",
        intro: "Welcome to the SeaGM study. Here's what happens next:",
        purposeTitle: "Study Purpose",
        purposeBody:
            "This study is the first step towards finding safe ways for seafarers with diabetes to work. We're gathering evidence to help determine whether current restrictions are appropriate.",
        formsTitle: "Daily Health Forms",
        formsBody: "You'll receive a text message every day at 5 PM with a link to complete your health form.",
        reportTitle: "What You'll Report",
        atSea: "Were you at sea/harbour today: Yes/No",
        ifYes: "If yes: All day or start/end times",
        glucose: "Any glucose issues: High/Low/None",
        other: "Any other issues: Free text (100 characters)",
        quickTitle: "Quick & Easy",
        quickBody: "The form takes just 2 minutes to complete and helps us track your health while at sea.",
        helpTitle: "Need Help?",
        helpBody: "If you have any questions or issues, please contact Dr Fariba Shojaee-Moradie:",
        direct: "Direct: 01483 688644",
        mobile: "Mobile: 07962215473",
        duration: "Study Duration: 1 year with 4 virtual meetings with study organisers",
        projectId: "Research Project ID: FHMS 23-24 062 | EGA/ERM ID 2469",
        logout: "Logout (You're Done!)",
    },
    formShell: {
        verifyingTitle: "Verifying Entry",
        verifyingDescription: "Please wait while we verify your entry...",
        checkingAccess: "Checking your access permissions...",
        notFoundTitle: "Entry Not Found",
        notFoundDescription: "The requested entry could not be found.",
        deniedTitle: "Access Denied",
        deniedDescription: "You don't have permission to access this entry.",
        title: (date) => `It is time for your daily entry for ${date}`,
        description: "Thank you for participating in the SeaGM study.",
        start: "Start Form",
        seeEntries: "See all entries",
        saveFailed: "Failed to save entry",
    },
    atSea: {
        question: "Were you at sea/harbour today?",
        yes: "I was at sea/harbour today",
        no: "I was not at sea/harbour today",
    },
    atSeaAllDay: {
        question: "Were you at sea/harbour all day?",
        yes: "I was at sea/harbour all day",
        no: "I was only at sea/harbour for part of the day",
    },
    intervals: {
        startRequired: "Select a start hour",
        endRequired: "Select an end hour",
        endAfterStart: "End hour must be after the start hour",
        atLeastOne: "Add at least one interval",
        noOverlap: "Intervals must not overlap",
        afterPrevious: "After previous interval",
        beforeNext: "Before next interval",
        question: "When were you at sea/harbour?",
        add: "Add another interval",
        title: (index) => `Interval ${index}`,
        remove: "Remove interval",
        startLabel: "Start hour",
        endLabel: "End hour",
        startPlaceholder: "Select start hour",
        endPlaceholder: "Select end hour",
    },
    duty: {
        atLeastOne: "Add at least one duty interval",
        noOverlap: "Duty intervals must not overlap",
        notOnWater: "Not on the water",
        chooseStartFirst: "Choose a start hour first",
        startOutside: "Start hour is outside sea/harbour time",
        endAfterStart: "End must be after the start",
        withinSeaTime: "Duty hours must sit within your time at sea/harbour",
        noneRecorded: "No sea/harbour hours recorded yet.",
        notAtSea: "You reported that you were not at sea/harbour today.",
        question: "When were you on duty?",
        todayLabel: "At sea/harbour today:",
        match: "Set to match",
        add: "Add another interval",
    },
    glucose: {
        question: "Did you experience any glucose issues today?",
        hint: "Click on one of the options below to select your answer",
        high: "High",
        highDescription: "Blood sugar above normal range",
        low: "Low",
        lowDescription: "Blood sugar below normal range",
        both: "Both",
        bothDescription: "Blood sugar above and below normal range",
        none: "None",
        noneDescription: "No glucose issues today",
    },
    notes: {
        tooLong: "Notes must be 100 characters or less",
        question: "Any additional comments?",
        hint: "Add any notes about your day (optional)",
        placeholder: "Any additional comments...",
        max: "Maximum 100 characters",
        count: (length) => `${length}/100 characters`,
    },
    steps: {
        atSea: "At Sea/Harbour Today",
        allDay: "All Day at Sea/Harbour",
        timeAtSea: "Time at Sea/Harbour",
        onDuty: "On Duty Hours",
        glucose: "Glucose Issues",
        notes: "Additional Notes",
        inProgress: "In Progress",
        completed: "Completed",
        skipped: "Skipped",
        pending: "Pending",
        loadError: "Error loading entry data",
        progress: (current, total) => `Step ${current} of ${total}`,
    },
    complete: {
        loadingTitle: "Loading...",
        loadingDescription: "Processing your submission",
        confirming: "Just confirming everything is saved...",
        entryTitle: "Entry Complete!",
        entryDescription: "Thank you for completing today's diary. Your information has been saved.",
        nextOn: (date) => `Your next entry will be available on ${date}.`,
        incomplete: (count) =>
            count === 1
                ? "You have 1 entry from a previous day that hasn't been completed yet."
                : `You have ${count} entries from previous days that haven't been completed yet.`,
        showEntries: "Show entries",
        showAll: (count) => `Show all ${count} entries`,
        completeMissing: "Complete Missing Entries",
        exit: "Exit",
        formTitle: "Form Complete!",
        formDescription:
            "Thank you for completing your daily health diary. Your information has been saved successfully.",
        exitForm: "Exit Form",
        seeEntries: "See all entries",
    },
    entries: {
        loadingTitle: "Loading entries",
        loadingDescription: "Retrieving your recent diary submissions.",
        pleaseWait: "Please wait while we gather your entries.",
        errorTitle: "Unable to load entries",
        errorDescription: "We hit an issue while loading your diary history.",
        title: "Your daily entries",
        description: "Select days to mark status or complete entries.",
        legend: "Legend:",
        complete: "Complete",
        incomplete: "Incomplete",
        today: "Today",
        emptyTitle: "No entries recorded yet.",
        emptyBody: "Once you start completing your daily diaries, they will appear on the calendar.",
        daysSelected: (count) => (count === 1 ? "1 day selected" : `${count} days selected`),
        clear: "Clear",
        creating: "Creating entry...",
        open: "Open Entry",
        createAndOpen: "Create & Open Entry",
        markAs: "Mark as:",
        atSea: "At Sea/Harbour",
        notAtSea: "Not at Sea/Harbour",
        noneSelected: "No dates selected",
        openOne: "Please select only one entry to open",
        createFailed: "Failed to create entry for this date",
        updateFailed: "Failed to update entries",
        markedNotAtSea: (count) =>
            count === 1 ? "1 entry marked as not at sea/harbour" : `${count} entries marked as not at sea/harbour`,
        markedAtSea: (count) =>
            count === 1
                ? "1 entry marked as at sea/harbour - please complete"
                : `${count} entries marked as at sea/harbour - please complete`,
        futureTitle: "Future Date Selected",
        futureBody: "You cannot add entries for future dates. Please select today or a past date.",
        ok: "OK",
    },
    loggedOut: {
        title: "Logged Out",
        description: "You have been logged out of the study",
    },
    unsubscribe: {
        loadingTitle: "Unsubscribing...",
        loadingDescription: "Please wait while we process your request",
        errorTitle: "Error",
        missingProfile: "Missing profile ID",
        failed: "Failed to unsubscribe",
        unexpected: "An unexpected error occurred",
        somethingWrong: "Something went wrong",
        title: "Unsubscribed",
        description: "You have been successfully unsubscribed from SMS notifications",
        body: "You will no longer receive daily reminder messages for the SeaGM Study.",
    },
};

const fr: GlucoseCopy = {
    languageLabel: "Langue",
    english: "English",
    french: "Français",
    longDate: "dddd D MMMM YYYY",
    weekdays: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
    common: {
        loading: "Chargement...",
        checkingAuth: "Vérification de votre accès",
        backHome: "Retour à l'accueil",
        tryAgain: "Réessayer",
        yes: "Oui",
        no: "Non",
        previous: "Précédent",
        next: "Suivant",
        saving: "Enregistrement...",
        submit: "Envoyer",
        sending: "Envoi...",
        selectOption: "Veuillez choisir une option avant de continuer",
    },
    home: {
        title: "Aider les marins diabétiques à travailler en mer et au port",
        subtitle:
            "Rejoignez notre étude et aidez-nous à créer des conditions de travail plus sûres et plus souples en mer et au port pour les personnes diabétiques.",
        join: "Rejoindre l'étude",
        learnMore: "En savoir plus",
        publicNote: "Cette page est publique. Vous pouvez découvrir l'étude sans vous connecter.",
        whatYouGet: "Ce que vous recevez",
        benefits: [
            "Moniteur de glucose Dexcom G7 gratuit pendant 12 mois (d'une valeur de plus de 1 200 £)",
            "Suivis réguliers avec l'équipe médicale",
            "Une contribution à de meilleures possibilités pour les futurs marins diabétiques",
            "Un meilleur suivi du glucose pendant 1 an",
        ],
        inclusion: "Critères d'inclusion",
        criteria: [
            "18 ans ou plus, toutes origines",
            "Marins nécessitant une insulinothérapie substitutive",
            "Titulaires d'un certificat ML5 ou ENG1 en cours de validité",
            "Marins sans ML5 ni ENG1 travaillant sur des embarcations de moins de dix mètres",
            "En mesure de porter un système de mesure continue du glucose (CGMS) pendant 12 mois, et disposés à le faire",
            "En mesure de consigner dans l'application Glucose Watch les glycémies en dehors de la plage normale et les activités en mer, et disposés à le faire",
        ],
        howItWorks: "Comment cela fonctionne",
        wearTitle: "Porter le moniteur",
        wearBody: "Nous fournissons un moniteur Dexcom G7 pendant 1 an",
        checkInTitle: "Message quotidien",
        checkInBody: "Envoyez-nous chaque jour à 17 h un message sur votre journée",
        meetingsTitle: "4 rendez-vous",
        meetingsBody: "4 rendez-vous en ligne dans l'année avec notre équipe",
        whyTitle: "Pourquoi nous faisons cela",
        whyIntro: "Les règlements maritimes actuels empêchent les marins diabétiques de :",
        restrictionsTitle: "Restrictions actuelles",
        restrictions: [
            "Travailler seul",
            "Assurer seul le quart",
            "Travailler en hauteur",
            "Être limité aux navires disposant d'un médecin",
        ],
        solutionTitle: "Notre solution",
        solutionBody:
            "Les moniteurs de glucose modernes comme le Dexcom G7 fournissent des alertes en temps réel et un suivi continu, ce qui pourrait rendre ces restrictions obsolètes. Notre étude vise à le démontrer avec des données recueillies en conditions réelles.",
        ctaTitle: "Envie de faire la différence ?",
        ctaBody: "Rejoignez notre étude et aidez-nous à améliorer les possibilités pour les marins diabétiques.",
        footer: "Université de Surrey • MCA • Financé par Seafarers' Charity",
    },
    getStarted: {
        title: "Étude SeaGM",
        subtitle:
            "Rejoignez notre étude pour nous aider à mieux comprendre la santé en mer et au port. Choisissez votre prochaine étape.",
        waitlistTitle: "Étape 1 : rejoindre la liste d'attente",
        waitlistDescription: "Manifestez votre intérêt et soyez informé de l'étude",
        waitlistPoints: [
            "Indiquez votre e-mail pour rejoindre la liste d'attente",
            "Soyez informé à l'ouverture de l'étude",
            "Sans engagement",
        ],
        joinWaitlist: "Rejoindre la liste d'attente",
        studyTitle: "Étape 2 : commencer l'étude",
        studyDescription: "Terminez l'inscription et commencez à participer",
        studyPoints: [
            "Complétez d'abord les documents de consentement",
            "Inscrivez-vous avec votre numéro de téléphone",
            "Commencez à recevoir les formulaires de santé quotidiens",
        ],
        begin: "Commencer l'inscription à l'étude",
        backHome: "Retour à l'accueil",
    },
    waitlist: {
        successTitle: "Vous êtes sur la liste d'attente",
        successDescription: "Nous vous préviendrons à l'ouverture de l'étude SeaGM.",
        backHome: "Retour à l'accueil",
        title: "Rejoindre la liste d'attente",
        description: "Indiquez vos coordonnées pour recevoir un message à l'ouverture de l'étude SeaGM",
        firstName: "Prénom",
        lastName: "Nom",
        email: "Adresse e-mail",
        joining: "Inscription...",
        join: "Rejoindre la liste d'attente",
        back: "Retour aux options",
    },
    login: {
        title: "Inscription à l'étude",
        description: "Terminez votre inscription pour participer à l'étude SeaGM",
        loading: "Chargement...",
        phoneRequired: "Le numéro de téléphone est requis",
        codeSent: "Code envoyé",
        codeSentDescription: "Un code de vérification a été envoyé sur votre téléphone.",
        sendFailed: "Échec de l'envoi du code",
        sendCode: "Envoyer le code",
    },
    verify: {
        codeLength: "Veuillez saisir le code à 6 chiffres",
        title: "Vérifiez votre téléphone",
        description: (phone) => `Saisissez le code à 6 chiffres envoyé au ${phone}`,
        resent: "Code envoyé",
        resentDescription: "Un nouveau code de vérification a été envoyé sur votre téléphone.",
        resendFailed: "Échec du renvoi du code",
        verifying: "Vérification...",
        verify: "Vérifier le code",
        resend: "Renvoyer le code",
        changePhone: "Changer de numéro",
    },
    profile: {
        checking: "Vérification de votre profil...",
        registeredTitle: "Vous êtes inscrit",
        registeredDescription: "Votre inscription à l'étude est terminée",
        continue: "Continuer vers l'étude",
        title: "Terminez votre inscription",
        description: "Créez votre profil pour rejoindre l'étude SeaGM",
        joining: "Inscription...",
        join: "Rejoindre l'étude",
    },
    instructions: {
        title: "À quoi s'attendre",
        intro: "Bienvenue dans l'étude SeaGM. Voici la suite :",
        purposeTitle: "Objectif de l'étude",
        purposeBody:
            "Cette étude est la première étape pour trouver des façons sûres de permettre aux marins diabétiques de travailler. Nous recueillons des données pour déterminer si les restrictions actuelles sont adaptées.",
        formsTitle: "Formulaires de santé quotidiens",
        formsBody: "Vous recevrez chaque jour à 17 h un SMS avec un lien pour compléter votre formulaire de santé.",
        reportTitle: "Ce que vous indiquerez",
        atSea: "Étiez-vous en mer ou au port aujourd'hui : oui/non",
        ifYes: "Si oui : toute la journée, ou les heures de début et de fin",
        glucose: "Problèmes de glucose : élevé/bas/aucun",
        other: "Autres remarques : texte libre (100 caractères)",
        quickTitle: "Rapide et simple",
        quickBody: "Le formulaire prend environ 2 minutes et nous aide à suivre votre santé en mer.",
        helpTitle: "Besoin d'aide ?",
        helpBody: "Pour toute question, contactez le Dr Fariba Shojaee-Moradie :",
        direct: "Ligne directe : 01483 688644",
        mobile: "Mobile : 07962215473",
        duration: "Durée de l'étude : 1 an, avec 4 rendez-vous en ligne avec les organisateurs",
        projectId: "Identifiant du projet : FHMS 23-24 062 | EGA/ERM ID 2469",
        logout: "Se déconnecter (c'est terminé)",
    },
    formShell: {
        verifyingTitle: "Vérification de la saisie",
        verifyingDescription: "Veuillez patienter pendant que nous vérifions votre saisie...",
        checkingAccess: "Vérification de vos droits d'accès...",
        notFoundTitle: "Saisie introuvable",
        notFoundDescription: "La saisie demandée est introuvable.",
        deniedTitle: "Accès refusé",
        deniedDescription: "Vous n'avez pas l'autorisation d'accéder à cette saisie.",
        title: (date) => `Il est temps de compléter votre saisie quotidienne du ${date}`,
        description: "Merci de votre participation à l'étude SeaGM.",
        start: "Commencer le formulaire",
        seeEntries: "Voir toutes les saisies",
        saveFailed: "Impossible d'enregistrer la saisie",
    },
    atSea: {
        question: "Étiez-vous en mer ou au port aujourd'hui ?",
        yes: "J'étais en mer ou au port aujourd'hui",
        no: "Je n'étais ni en mer ni au port aujourd'hui",
    },
    atSeaAllDay: {
        question: "Étiez-vous en mer ou au port toute la journée ?",
        yes: "J'étais en mer ou au port toute la journée",
        no: "Je n'étais en mer ou au port qu'une partie de la journée",
    },
    intervals: {
        startRequired: "Choisissez une heure de début",
        endRequired: "Choisissez une heure de fin",
        endAfterStart: "L'heure de fin doit être postérieure à l'heure de début",
        atLeastOne: "Ajoutez au moins un intervalle",
        noOverlap: "Les intervalles ne doivent pas se chevaucher",
        afterPrevious: "Après l'intervalle précédent",
        beforeNext: "Avant l'intervalle suivant",
        question: "Quand étiez-vous en mer ou au port ?",
        add: "Ajouter un autre intervalle",
        title: (index) => `Intervalle ${index}`,
        remove: "Retirer l'intervalle",
        startLabel: "Heure de début",
        endLabel: "Heure de fin",
        startPlaceholder: "Choisir l'heure de début",
        endPlaceholder: "Choisir l'heure de fin",
    },
    duty: {
        atLeastOne: "Ajoutez au moins un intervalle de quart",
        noOverlap: "Les intervalles de quart ne doivent pas se chevaucher",
        notOnWater: "Pas en mer ni au port",
        chooseStartFirst: "Choisissez d'abord une heure de début",
        startOutside: "L'heure de début est en dehors du temps en mer ou au port",
        endAfterStart: "L'heure de fin doit être après l'heure de début",
        withinSeaTime: "Les heures de quart doivent se situer dans votre temps en mer ou au port",
        noneRecorded: "Aucune heure en mer ou au port n'a encore été enregistrée.",
        notAtSea: "Vous avez indiqué que vous n'étiez ni en mer ni au port aujourd'hui.",
        question: "Quand étiez-vous de quart ?",
        todayLabel: "En mer ou au port aujourd'hui :",
        match: "Utiliser les mêmes horaires",
        add: "Ajouter un autre intervalle",
    },
    glucose: {
        question: "Avez-vous eu des problèmes de glucose aujourd'hui ?",
        hint: "Choisissez l'une des options ci-dessous",
        high: "Élevé",
        highDescription: "Glycémie au-dessus de la plage normale",
        low: "Bas",
        lowDescription: "Glycémie en dessous de la plage normale",
        both: "Les deux",
        bothDescription: "Glycémie au-dessus et en dessous de la plage normale",
        none: "Aucun",
        noneDescription: "Pas de problème de glucose aujourd'hui",
    },
    notes: {
        tooLong: "Les notes ne doivent pas dépasser 100 caractères.",
        question: "D'autres commentaires ?",
        hint: "Ajoutez des notes sur votre journée (facultatif)",
        placeholder: "Autres commentaires...",
        max: "100 caractères maximum",
        count: (length) => `${length}/100 caractères`,
    },
    steps: {
        atSea: "En mer ou au port aujourd'hui",
        allDay: "Toute la journée en mer ou au port",
        timeAtSea: "Temps en mer ou au port",
        onDuty: "Heures de quart",
        glucose: "Problèmes de glucose",
        notes: "Notes complémentaires",
        inProgress: "En cours",
        completed: "Terminée",
        skipped: "Sans objet",
        pending: "En attente",
        loadError: "Erreur de chargement de la saisie",
        progress: (current, total) => `Étape ${current} sur ${total}`,
    },
    complete: {
        loadingTitle: "Chargement...",
        loadingDescription: "Enregistrement de votre formulaire",
        confirming: "Nous vérifions que tout est bien enregistré...",
        entryTitle: "Saisie terminée",
        entryDescription: "Merci d'avoir complété le journal d'aujourd'hui. Vos informations ont été enregistrées.",
        nextOn: (date) => `Votre prochaine saisie sera disponible le ${date}.`,
        incomplete: (count) =>
            count === 1
                ? "Vous avez 1 saisie d'un jour précédent qui n'est pas encore terminée."
                : `Vous avez ${count} saisies de jours précédents qui ne sont pas encore terminées.`,
        showEntries: "Afficher les saisies",
        showAll: (count) => `Afficher les ${count} saisies`,
        completeMissing: "Compléter les saisies manquantes",
        exit: "Quitter",
        formTitle: "Formulaire terminé",
        formDescription:
            "Merci d'avoir complété votre journal de santé quotidien. Vos informations ont bien été enregistrées.",
        exitForm: "Quitter le formulaire",
        seeEntries: "Voir toutes les saisies",
    },
    entries: {
        loadingTitle: "Chargement des saisies",
        loadingDescription: "Récupération de vos journaux récents.",
        pleaseWait: "Veuillez patienter pendant que nous rassemblons vos saisies.",
        errorTitle: "Impossible de charger les saisies",
        errorDescription: "Un problème est survenu pendant le chargement de votre historique.",
        title: "Vos saisies quotidiennes",
        description: "Sélectionnez des jours pour indiquer où vous étiez, ou pour compléter une saisie.",
        legend: "Légende :",
        complete: "Terminée",
        incomplete: "Incomplète",
        today: "Aujourd'hui",
        emptyTitle: "Aucune saisie pour le moment.",
        emptyBody: "Lorsque vous compléterez vos journaux quotidiens, ils apparaîtront sur le calendrier.",
        daysSelected: (count) => (count === 1 ? "1 jour sélectionné" : `${count} jours sélectionnés`),
        clear: "Effacer",
        creating: "Création de la saisie...",
        open: "Ouvrir la saisie",
        createAndOpen: "Créer et ouvrir la saisie",
        markAs: "Marquer comme :",
        atSea: "En mer ou au port",
        notAtSea: "Pas en mer ni au port",
        noneSelected: "Aucune date sélectionnée",
        openOne: "Veuillez sélectionner une seule saisie à ouvrir",
        createFailed: "Impossible de créer une saisie pour cette date",
        updateFailed: "Impossible de mettre à jour les saisies",
        markedNotAtSea: (count) =>
            count === 1
                ? "1 saisie marquée « ni en mer ni au port »"
                : `${count} saisies marquées « ni en mer ni au port »`,
        markedAtSea: (count) =>
            count === 1
                ? "1 saisie marquée comme en mer ou au port - veuillez la compléter"
                : `${count} saisies marquées comme en mer ou au port - veuillez les compléter`,
        futureTitle: "Date future sélectionnée",
        futureBody:
            "Vous ne pouvez pas ajouter de saisie pour une date future. Choisissez aujourd'hui ou une date passée.",
        ok: "OK",
    },
    loggedOut: {
        title: "Déconnecté",
        description: "Vous avez été déconnecté de l'étude",
    },
    unsubscribe: {
        loadingTitle: "Désinscription...",
        loadingDescription: "Veuillez patienter pendant le traitement de votre demande",
        errorTitle: "Erreur",
        missingProfile: "Identifiant de profil manquant",
        failed: "Échec de la désinscription",
        unexpected: "Une erreur inattendue s'est produite",
        somethingWrong: "Un problème est survenu",
        title: "Désinscription confirmée",
        description: "Vous avez été désinscrit des notifications par SMS",
        body: "Vous ne recevrez plus les rappels quotidiens de l'étude SeaGM.",
    },
};

export const glucoseCopy: Record<GlucoseLocale, GlucoseCopy> = { en, fr };

export function isGlucoseLocale(value: string | null | undefined): value is GlucoseLocale {
    return value === "en" || value === "fr";
}
